import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAPSFolderContent, fetchApsDownload, fetchApsExtractFileCCT, fetchApsExtractFiles, fetchApsExtractFormData, fetchApsFormList, fetchApsHubs, fetchApsIssues, fetchApsProjects, fetchApsRFIs, fetchApsScheduleExtraction, fetchApsSubmittals, fetchApsUpload, fetchExtractFileExcel } from "../services/apsService";
import useConnectionStore from "../store/useConnectors";


// GET HUBS
export const useApsGetHubs = () =>{
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  return useQuery({
    queryKey: ["hubs", connectionId],
    queryFn: () => fetchApsHubs(connectionId),
    enabled: !!connectionId,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}
  

// GET PROJECT
export const useApsGetProjects = (hubsId) => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  return useQuery({
    queryKey: ["projects", connectionId, hubsId],
    queryFn: () => fetchApsProjects(connectionId, hubsId),
    enabled: !!connectionId && !!hubsId,
    staleTime: 5 * 60 * 1000,
  });
}

// EXTRACT ISSUES
export const useAPSExtractIssues = () => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return fetchApsIssues(connectionId, payload);
    },
    onSuccess: () => {
      // refresh extracttion list
      queryClient.invalidateQueries({ queryKey: ['extraction'] });
    }
  });
};

// GET SUBMITTALS
export const useAPSExtractSubittals = () => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      // console.log({ payload, connectionId });
      return fetchApsSubmittals(connectionId, payload);
    },
    onSuccess: () => {
      // refresh extracttion list
      queryClient.invalidateQueries({ queryKey: ['extraction'] });
    }
  });
};

// EXTRACT RFIS
export const useAPSExtractRFIs = () => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return fetchApsRFIs(connectionId, payload);
    },
    onSuccess: () => {
      // refresh extracttion list
      queryClient.invalidateQueries({ queryKey: ['extraction'] });
    }
  });
};

// GET FORM LIST
export const useApsGetFormList= (projectId) => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  return useQuery({
    queryKey: ["forms", connectionId, projectId],
    queryFn: () => fetchApsFormList(connectionId, projectId),
    enabled: false,
    staleTime: 5 * 60 * 1000,
    select: (response) => response.data
  });
}

// EXTRACT FORMS
export const useAPSExtractFormData = () => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return fetchApsExtractFormData(connectionId, payload);
    },
    onSuccess: () => {
      // refresh extracttion list
      queryClient.invalidateQueries({ queryKey: ['extraction'] });
    }
  });
};

export const useAPSRootFolders = ({hub_id, project_id}) =>{
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  return useQuery({
      queryKey: ["aps", "folders", "root", connectionId, {hub_id, project_id}],
      queryFn: () => fetchAPSFolderContent(connectionId, {hub_id, project_id}),
      enabled: !!hub_id && !!project_id,
      staleTime: 5 * 60 * 1000, // 5 min
  });
}

export const useAPSFolderItems = ({hub_id, project_id, folder_id}) => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  return useQuery({
      queryKey: ["aps", "folder", connectionId, {hub_id, project_id, folder_id}],
      queryFn: () => fetchAPSFolderContent(connectionId, {hub_id, project_id, folder_id}),
      enabled: !!hub_id && !!project_id || !!folder_id,
      staleTime: 5 * 60 * 1000,
  });
}

// EXTRACT FILES
export const useAPSExtractFile = () => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return fetchApsExtractFiles(connectionId, payload);
    },
    onSuccess: () => {
      // refresh extracttion list
      queryClient.invalidateQueries({ queryKey: ['extraction'] });
    }
  });
};

// EXTRACT FILES CCT
export const useAPSExtractFileCCT = () => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return fetchApsExtractFileCCT(connectionId, payload);
    },
    onSuccess: () => {
      // refresh extracttion list
      queryClient.invalidateQueries({ queryKey: ['extraction'] });
    }
  });
};

// EXTRACT EXCEL
export const useExtractFileExcel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      console.log("payload from hook", payload)
      return fetchExtractFileExcel(payload);
    },
    onSuccess: () => {
      // refresh extracttion list
      queryClient.invalidateQueries({ queryKey: ['extraction'] });
    }
  });
};

// GET DOWNLOAD FILE
export const useApsDownloadFile= ({type, id}) => {
  return useQuery({
    queryKey: ["aps-file-download", type, id],
    queryFn: () => fetchApsDownload({type, id}),
    enabled: false,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}


// EXTRACT EXCEL
export const useApsUploadFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return fetchApsUpload(payload);
    },
    onSuccess: () => {
      // refresh extracttion list
      queryClient.invalidateQueries({ queryKey: ['extraction'] });
    }
  });
};

// EXTRACT SCHEDULE
export const useExtractSchedule = () => {
  const connectionId = useConnectionStore((s) => s.activeConnectionId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return fetchApsScheduleExtraction(connectionId, payload);
    },
    onSuccess: () => {
      // refresh extracttion list
      queryClient.invalidateQueries({ queryKey: ['schedule-list'] });
    }
  });
};
  
